import type { Dispatch } from 'umi';
import type { TDispatchFun } from '@/pages/datalab/components/SortableDrag/configration';
import { CHART_SETTINGS_NAMESPACE } from '@/pages/datalab/models/namespaces';
import { namespace } from '../../../model';
import type {
  ISearchDispatchAdapter,
  ISearchModelState,
  TSearchFieldZoneKey,
} from '../types';
import type { CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';

/**
 * Search 写侧适配层：UI / Hook 只依赖语义化 API，不直接拼 `${namespace}/save`。
 * model 字段重命名时改 FIELD_ZONE_MODEL_KEY 与本文件即可。
 */
/** zone 语义 key → DVA model 字段名；model 重构时只改此表 */
export const FIELD_ZONE_MODEL_KEY: Record<
TSearchFieldZoneKey,
keyof Pick<
ISearchModelState,
| 'columnFilters'
| 'columnMetrics'
| 'columnDimensions'
| 'columnX'
| 'columnShowDimensions'
| 'columnRawColumns'
>
> = {
  filters: 'columnFilters',
  metrics: 'columnMetrics',
  dimensions: 'columnDimensions',
  xAxis: 'columnX',
  showDimensions: 'columnShowDimensions',
  rawColumns: 'columnRawColumns',
};

export const createSearchDispatchAdapter = (
  dispatch: Dispatch,
): ISearchDispatchAdapter => {
  /** Sortable 等遗留组件使用的通用 dispatch 包装 */
  const dispatchFun: TDispatchFun = (
    path: string,
    payload: Record<string, unknown>,
    callback?: Function,
  ) => {
    dispatch({
      type: `${namespace}/${path}`,
      payload,
      callback: callback as ((payload: unknown) => void) | undefined,
    });
  };

  return {
    /** 批量写入 model 任意字段 */
    save: payload => {
      dispatch({
        type: `${namespace}/save`,
        payload,
      });
    },

    /** 按语义 zone 更新单个字段区（批量添加等场景预留） */
    saveFields: (zone, columns) => {
      const modelKey = FIELD_ZONE_MODEL_KEY[zone];
      dispatch({
        type: `${namespace}/save`,
        payload: { [modelKey]: columns },
      });
    },

    /** 更新结果行数上限 */
    saveRowLimit: rowLimit => {
      dispatch({
        type: `${namespace}/save`,
        payload: { rowLimit },
      });
    },

    /** 折叠 Search 区显隐持久化 */
    saveVisibleSearch: visibleSearch => {
      dispatch({
        type: `${namespace}/save`,
        payload: { visibleSearch },
      });
    },

    /** 用户分群筛选保存，并通知 chartSettings 联动 */
    saveUserFilters: groups => {
      const newColumnUserFilters = groups?.map(group => {
        const filters = group?.filters ?? [];
        return {
          ...group,
          // 仅选「全部用户」时标记 is_all_users，与后端口径一致
          is_all_users: filters?.length === 1 && !filters?.[0]?.value,
        };
      });
      dispatch({
        type: `${namespace}/save`,
        payload: { columnUserFilters: newColumnUserFilters },
      });
      // 用户分群变更需同步 chartSettings，避免表格列/排序与用户筛选脱节
      dispatch({
        type: `${CHART_SETTINGS_NAMESPACE}/watchUserFiltersChange`,
        payload: { userFilters: newColumnUserFilters },
      });
    },

    /** 触发数据分析计算 */
    runAnalysis: (params, callback) => {
      dispatch({
        type: `${namespace}/getDataAnlysis`,
        payload: params,
        callback,
      });
    },

    /** 预览 SQL 文本 */
    previewSql: callback => {
      dispatch({
        type: `${namespace}/previewSQL`,
        payload: {},
        callback,
      });
    },

    /** 清空查询条件与字段区 */
    clearQuery: (payload = {}) => {
      dispatch({
        type: `${namespace}/clearQuery`,
        payload,
      });
    },

    /** 取消进行中的分析 */
    cancelAnalysis: options => {
      dispatchFun('cancelAnalysis', options);
    },

    /** 自定义字段编辑保存 */
    updateCustomColumn: (updateColumn: CustomColumn) => {
      dispatchFun('updateCustomColumn', { updateColumn });
    },

    /** 拖拽态写入 model（currentDrag / errorDrag） */
    saveDrag: payload => {
      dispatchFun('saveDrag', payload);
    },

    dispatchFun,
  };
};
